<script setup lang="ts">
const props = defineProps<{
    nutzerId: string
    confirmationToken: string
}>()

type PasswortAendernForm = {
    neuesPasswort: string
    neuesPasswortRepeat: string
}

const { handleSubmit } = useForm<PasswortAendernForm>()

const fields: {
    [Property in keyof PasswortAendernForm]: any
} = {
    neuesPasswort: useField('neuesPasswort', (value: any) => {
        if (!value) return 'Neues Passwort muss angegeben werden.'
        if (value.length < 8) return 'Neues Passwort muss mind. 8 Zeichen haben.'
        return true
    }),
    neuesPasswortRepeat: useField('neuesPasswortRepeat', (value) => {
        if (value !== fields.neuesPasswort.value.value) return 'Passwörter müssen übereinstimmen.'
        return true
    })
}

const showNeuesPasswort = ref(false)
const showNeuesPasswortRepeat = ref(false)
const isPasswordChanged = ref(false)

const nutzerStore = useNutzerStore()

const submitLoading = computed(() => nutzerStore.isMethodLoading('passwortAendern'))

const submit = handleSubmit(async () => {
    try {
        await nutzerStore.passwortAendern({
            nutzer: `/api/nutzende/${props.nutzerId}`,
            confirmationToken: props.confirmationToken,
            password: fields.neuesPasswort.value.value
        })
        isPasswordChanged.value = true
    } catch (e: unknown) {
        console.error(e)
    }
})
</script>

<template>
    <v-row justify="center" class="mt-6">
        <v-col cols="12" sm="8" md="6" lg="6" xl="4">
            <h1 class="text-center mb-6">Neues Passwort festlegen</h1>

            <v-form @submit.prevent="submit" :disabled="isPasswordChanged">
                <!-- Neues Passwort -->
                <v-text-field
                    class="mb-4"
                    v-model.trim="fields.neuesPasswort.value.value"
                    label="Neues Passwort"
                    :type="showNeuesPasswort ? 'text' : 'password'"
                    :append-inner-icon="showNeuesPasswort ? 'mdi-eye' : 'mdi-eye-off'"
                    @click:append-inner="showNeuesPasswort = !showNeuesPasswort"
                    :error-messages="fields.neuesPasswort.errorMessage.value"
                    required
                    autocomplete="new-password"
                />

                <!-- Neues Passwort wiederholen -->
                <v-text-field
                    class="mb-4"
                    v-model.trim="fields.neuesPasswortRepeat.value.value"
                    label="Neues Passwort wiederholen"
                    :type="showNeuesPasswortRepeat ? 'text' : 'password'"
                    :append-inner-icon="showNeuesPasswortRepeat ? 'mdi-eye' : 'mdi-eye-off'"
                    @click:append-inner="showNeuesPasswortRepeat = !showNeuesPasswortRepeat"
                    :error-messages="fields.neuesPasswortRepeat.errorMessage.value"
                    required
                    autocomplete="new-password"
                />

                <v-alert class="w-100 text-muted mb-3" v-if="!isPasswordChanged">
                    <b>Hinweis:</b>
                    <ul class="mb-0 pl-5">
                        <li>
                            Bitte wähle ein sicheres Passwort mit mindestens 12 Zeichen.
                        </li>
                    </ul>
                </v-alert>

                <v-row>
                    <v-col cols="12">
                        <v-btn
                            type="submit"
                            color="#00527e"
                            class="mb-3"
                            block
                            :disabled="nutzerStore.isLoading || isPasswordChanged"
                            :loading="submitLoading"
                        >
                            Passwort festlegen
                        </v-btn>

                        <v-alert
                            v-if="isPasswordChanged"
                            class="mb-2"
                            icon="mdi-check-circle-outline"
                            type="success"
                            prominent
                        >
                            <b>Passwort erfolgreich geändert</b>
                            <ul class="mb-0 pl-5">
                                <li>
                                    Du kannst dich jetzt mit deinem neuen Passwort anmelden.
                                </li>
                            </ul>
                        </v-alert>

                        <v-btn
                            :to="{ name: 'login' }"
                            variant="plain"
                            color="#00527e"
                            block
                        >
                            Zurück zur Anmeldung
                        </v-btn>
                    </v-col>
                </v-row>
            </v-form>
        </v-col>
    </v-row>
</template>

<style scoped>
.v-form {
    width: 100%;
}
</style>
